import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';

// External components
import Cropper from 'cropperjs';

import { getUUID, makePromiseCancelable } from '../../../services/AppUtils';

// App Config
import AppConfiguration from '../../../services/AppConfiguration';
import appRoutes from '../../../constants/appRoutes';
import {
  NO_DATA_FOUND_FOR_IMAGE_ERROR,
  CANCELED_AUTO_RECOGNITION_ERROR,
  DEFAULT,
  NO_ERROR,
} from '../../../constants/errorConstants';

// Services
import CropAreaService, { getAnnotationIndex } from '../../../services/CropAreaService';

// Store actions
import {
  setError,
  setCropEndpointCalled,
  setCropAreaSize,
  setCropData,
  setMD5,
  setIsAnyRegionDeleted,
  setInitialRegionData,
  setAnnotationIndex,
  setSelectedCropRegion,
  setDrawingMode,
  setLoading,
  setCroppedImageUrl,
  setVerticalCropSelected,
  setNextButtonTooltipDisplay,
  setRotationAmount,
  setRotationMode,
} from '../../../actions/crop';

import { setResultRequestFlag, setResultData } from '../../../actions/result';
import { uploadImageUrl } from '../../../actions/upload';

// Common components
import CropToolbar from '../../common/CropToolbar/CropToolbar';

// Crop components
import CropArea from '../CropArea/CropArea';
import RotatedImage from '../RotatedImage/RotatedImage';

// Component styles
import './cropAreaContainer.scss';

/**
 * This container is linked to the store `upload` and the `crop` key and it wraps the CropArea and
 * the CropToolbar component.
 * - Toogle the loading component state when the BE request start.
 * - Update the crop data information once we have the data from the BE.
 * - Gets the cropped image url and redirect to the result page when the search button is clicked.
 * @extends {Component}
 */
class CropAreaContainer extends React.Component {
  constructor(props) {
    super(props);

    /**
     * Instance of the App configuration data.
     * @type {Object}
     */
    this.appConfig = AppConfiguration.getConfig();

    /**
     * Instance of the CropAreaService service. It will get the image crop data from the BE.
     * @type {CropAreaService}
     */
    this.cropAreaService = new CropAreaService();

    /**
     * Function that cancels a pending "getImageCrops" operation / promise.
     * @type {Function|Null}
     */
    this.cancelGetImageCrops = null;

    /**
     * Constraint for the crop size (in pixels). This value determines the minimum
     * size the crop should have in both dimensions, height and width.
     * @type {Number}
     */
    this.minimumCropSize = this.appConfig.minimumCropSize;

    /**
     * Internal component state.
     * The sole purpose of using this state and not the redux container is because the
     * RegionSelect component needs to be tied to a state object in order to work, and it will
     * always update it whenever any crop region is resized, each pixel, every a few miliseconds.
     * That might cause serious performance penalties if using all the bold and dummy stuff Redux
     * needs.
     * So this state here will only contain the crop regions so the RegionSelect can interact with
     * them. But after we detect the user interaction is over, we automatically save this state in
     * the Redux store.
     * @type {Object}
     * @property {Object[]} cropData
     */
    this.state = {
      cropData: props.cropData,
    };

    /**
     * Hex value for the color that will be used to fill the empty spaces left after the image is rotated.
     * @type {String}
     */
    this.rotatedImageFillColor = this.appConfig.rotatedImageFillColor;

    // Context binding for function.
    this.clearError = this.clearError.bind(this);
    this.identifyTextAndSetCropRegions = this.identifyTextAndSetCropRegions.bind(this);
    this.handleCancelRotation = this.handleCancelRotation.bind(this);
    this.handleCropButtonInteraction = this.handleCropButtonInteraction.bind(this);
    this.handleIncrementalRotation = this.handleIncrementalRotation.bind(this);
    this.handleSearchButtonInteraction = this.handleSearchButtonInteraction.bind(this);
    this.handleRotateButtonInteraction = this.handleRotateButtonInteraction.bind(this);
    this.isCropRegionBeingAltered = this.isCropRegionBeingAltered.bind(this);
    this.onCropRegionChange = this.onCropRegionChange.bind(this);
    this.selectRegion = this.selectRegion.bind(this);
    this.unselectAllRegions = this.unselectAllRegions.bind(this);
    this.onImageResize = this.onImageResize.bind(this);
    this.isSelectedCropRegionVertical = this.isSelectedCropRegionVertical.bind(this);
    this.onNextButtonTooltipClosed = this.onNextButtonTooltipClosed.bind(this);
    this.isAnyCropRegionOnEdition = this.isAnyCropRegionOnEdition.bind(this);
    this.isAnyCropRegionNew = this.isAnyCropRegionNew.bind(this);
    this.getSelectedRegion = this.getSelectedRegion.bind(this);
    this.onDelete = this.onDelete.bind(this);
  }

  /**
   * Whenever the props are updated, this will take the cropData prop and update the
   * internal state of this component. That's a side effect of dettaching the state of the
   * component into the native state and the Redux store. See the explanation in the docs
   * for `this.state` above.
   * @param {Object} nextProps
   */
  componentWillReceiveProps(nextProps) {
    this.setState({
      cropData: nextProps.cropData,
    });
  }

  /**
   * Clear store errorCode. Next time the component is loaded the error code should be empty.
   * - Clear store errorCode. Next time the component is loaded the error code should be empty.
   * - If exist destroy cropper instance.
   * - Resets the rotation mode by disabling it and setting the rotation amount to 0
   */
  componentWillUnmount() {
    this.clearError();

    if (this.cropper) {
      this.cropper.destroy();
    }

    this.props.setRotationMode(false);
    this.props.setRotationAmount(0);
  }

  /**
   * Function called whenever the regions in the CropArea component change. This change can be a
   * new region created, one being redimensioned or one being selected.
   * The function receives the whole array of regions, not just the one being changed. It works
   * that way because it's in charge of a 3rd party component (RegionSelect).
   *
   * Internally, it will take all the regions, update their data if necessary, update the component
   * state with the regions with the tweaked data, and if the user is no longer altering said
   * regions, they will be saved in the store.
   * @param {Object[]} regions
   */
  onCropRegionChange(regions) {
    const [cropData] = [this.props.cropData];
    const previousSelectedRegion = this.props.selectedCropRegion;

    // Update the state with new data if the component is in drawing mode
    if (this.props.drawingMode) {
      regions = this.updateRegionRequirements(regions);

      this.selectNewCropIfAny(regions);

      this.setState({
        cropData: regions,
      });

      if (!this.isAnyCropRegionChanging(regions)) {
        regions = this.removeExtraDataFromRegions(regions);
        this.props.setCropData(regions);
        this.toggleDrawingMode();
      }
    } else if (this.isAnyCropRegionOnEdition(regions) && cropData.length === regions.length) {
      /* When drawing mode is not active, if any region is being changed and no region is created,
         then set the state with the new resized data */
      const selectedRegion = this.getSelectedRegion(regions);

      // Select region if the changing region is not already selected.
      if (selectedRegion !== previousSelectedRegion) {
        if (previousSelectedRegion > -1) {
          this.removeCropRegionStyle(regions, previousSelectedRegion);
        }
        this.selectRegion(regions, selectedRegion);
      }

      regions = this.updateRegionRequirements(regions);
      this.setState({
        cropData: regions,
      });
    } else {
      // When drawing mode is not active, let the user select a region
      for (let i = 0; i < regions.length; i++) {
        const region = regions[i];

        // The isChanging property determines which region is being selected
        if (this.regionContainsChangingProperty(region)) {
          // Do not do anything if the region is currently changing
          if (this.isCropRegionChanging(region)) {
            break;
          }

          /* When the user clicks outside an existing region, the 3rd party lib tries to create a new region.
             Since drawing mode is not active, the new region should not be created.
             We use this situation, to unselect all regions */
          if (cropData.length < regions.length) {
            if (previousSelectedRegion !== -1) {
              this.unselectAllRegions();
            }
            break;
          } else if (previousSelectedRegion === i) {
            // Do not do anything if the uer clicked on the currently selected region again
            break;
          }

          // Remove all styles from the previous selected region
          if (previousSelectedRegion !== -1) {
            this.removeCropRegionStyle(cropData, previousSelectedRegion);
          }

          this.selectRegion(cropData, i);
          break;
        }
      }

      /**
       * Save to the store ONLY modified regions and regions that contain the needed data to work.
       * `regionContainsNeededData` was added because if a user clicks and releases the mouse
       * button(thus creating a dot instead of a rectangular region) the region object created
       * has only two attributes. { new: false, isChanging: false }
       */
      if (!this.isAnyCropRegionNew(regions) && this.regionsContainNeededData(regions)) {
        regions = this.removeExtraDataFromRegions(regions);
        this.props.setCropData(regions);
      }
    }
  }

  /**
   * Callback for when the image in CropArea component gets resized for some reason.
   * It receives a DOMRectReadOnly object with position and dimension data. This method
   * will extract height and width and call the `setCropAreaSize` action in order to
   * update the store. That state object will later be used to perform the calculations
   * required in order to correctly place the crop regions on top of the image.
   * @param {Object}
   * @property {Number} entry.width
   * @property {Number} entry.height
   */
  onImageResize({ entry: dimensionData }) {
    const { width, height } = dimensionData;
    this.props.setCropAreaSize({ width, height });
  }

  /**
   * Callback to execute when the user closes the Next Button Tooltip.
   * This tooltip will be closed until the user uploads a new image
   */
  onNextButtonTooltipClosed() {
    this.props.setNextButtonTooltipDisplay(false);
  }

  /**
   * This method is called when a selected region needs to be deleted from the cropData array.
   * @param {Number} index the region index to delete.
   */
  onDelete(index) {
    const cropBoxesWithDeletion = [...this.props.cropData];
    cropBoxesWithDeletion.splice(index, 1);
    this.props.setIsAnyRegionDeleted(true);
    this.props.setSelectedCropRegion({ selectedCropRegion: -1, cropData: cropBoxesWithDeletion });
  }

  /**
   * It gets the selected region from the regions array.
   * @param {Array} regions All the crop regions.
   * @returns {Number}
   */
  getSelectedRegion(regions) {
    let selectedIndex = -1;

    regions.forEach((region, index) => {
      if (this.isCropRegionChanging(region)) {
        selectedIndex = index;
      }
    });
    return selectedIndex;
  }

  /**
   * Removes all specific styling porperties from the regions provided.
   * The specific styles are stored in the data.regionStyle property
   *
   * @param {Object[]} regions All the crop regions
   * @returns {Object[]} the unstyled regions
   */
  getUnstyledRegions(regions) {
    return regions.map((region) => {
      if (region.data) {
        delete region.data.regionStyle;
      }
      return region;
    });
  }

  /**
   * Process all regions and determine which has the largest surface.
   *
   * @param {Object[]} regions All the regions to process
   * @returns {Number} The index of the largest Crop Region
   */
  getLargestCropRegionIndex(regions) {
    let largestSurface = 0;
    let index = 0;

    regions.forEach((region, i) => {
      const regionSurface = region.width * region.height;
      if (regionSurface > largestSurface) {
        largestSurface = regionSurface;
        index = i;
      }
    });

    return index;
  }

  /**
   * This method returns an object with all the Toolbar proptypes the component needs.
   */
  getCropToolbarProps() {
    return {
      isRotationModeActive: this.props.rotationMode,
      isRotateButtonEnabled: !this.props.loading && !this.props.drawingMode,
      isManualCropButtonActive: true,
      isSearchButtonActive: !!this.props.cropData.length &&
                            this.props.selectedCropRegion !== -1 &&
                            !this.props.drawingMode,
      isManualCropButtonHighlighted: this.props.drawingMode,
      loading: this.props.loading,
      showRotateTooltip: this.props.verticalCropSelected,
      showNextButtonEnabledTooltip: this.props.showNextButtonEnabledTooltip,
      onCancelRotation: this.handleCancelRotation,
      onCropButtonClick: this.handleCropButtonInteraction,
      onIncrementalRotationButtonClick: this.handleIncrementalRotation,
      onRotateButtonClick: this.handleRotateButtonInteraction,
      onSearchButtonClick: this.handleSearchButtonInteraction,
      onNextButtonTooltipClosed: this.onNextButtonTooltipClosed,
    };
  }

  /**
   * This method returns an object with all the CropArea proptypes the component needs.
   */
  getCropAreaProps() {
    return {
      loading: this.props.loading,
      cropRegions: this.state.cropData,
      drawingMode: this.props.drawingMode,
      imageUrl: this.props.imageUrl,
      errorCode: this.props.errorCode,
      rotationAmount: this.props.rotationAmount,
      rotationMode: this.props.rotationMode,
      selectedCropRegion: this.props.selectedCropRegion,
      cropAreaSize: this.props.cropAreaSize,
      onImageLoaded: this.identifyTextAndSetCropRegions,
      onImageResize: this.onImageResize,
      onCropRegionChange: this.onCropRegionChange,
      onErrorClose: this.clearError,
      onDelete: this.onDelete,
    };
  }

  /**
   * Returns an object with all the props the RotatedImage component needs.
   * @return {Object}
   * @property {String} imageUrl
   * @property {String} fillColor
   * @property {Number} rotationAmount
   * @property {Number} sizeBoundries.height
   * @property {Number} sizeBoundries.width
   */
  getRotatedImageProps() {
    return {
      imageUrl: this.props.imageUrl,
      fillColor: this.rotatedImageFillColor,
      rotationAmount: this.props.rotationAmount,
      sizeBoundries: this.props.cropAreaSize,
    };
  }

  /**
   * Callback that is executed when the Cropperjs image is instantiated.
   * - Takes from the store the current crop area.
   * - Get the canvas area of the crop area and its base64.
   * - Update the store with the cropped image objecturl and redirect to the result page.
   */
  getCroppedImage() {
    const cropArea = this.props.cropData[this.props.selectedCropRegion];
    this.props.setAnnotationIndex(this.props.selectedCropRegion);
    const translatedCropArea = this.translateCropRegionDimensions(cropArea);
    try {
      const annotationIndex = getAnnotationIndex(
        this.props.isAnyRegionDeleted, this.props.annotationIndex,
        this.props.initialRegionData, cropArea
      );
      this.props.setAnnotationIndex(annotationIndex);
    } catch (err) {
      this.props.setAnnotationIndex(-1);
    }

    this.cropper.setCropBoxData({
      left: translatedCropArea.x,
      top: translatedCropArea.y,
      width: translatedCropArea.width,
      height: translatedCropArea.height,
    });

    const canvasData = this.cropper.getCroppedCanvas();

    // Get the binary from the cropped image canvas.
    canvasData.toBlob((blob) => {
      // Get the object url from the cropped image binary.
      this.props.setCroppedImageUrl(window.URL.createObjectURL(blob));

      this.props.setResultData({});

      this.props.setResultRequestFlag(true);

      this.props.redirectToResult();
    });
  }

  /**
   * Returns the data from the autocrop BE endpoint. It also sets a function in
   * `this.cancelGetImageCrops` in order to cancel the main XHR promise while it's still
   * working.
   * @returns {Promise}
   */
  getImageCrops() {
    const imageCropsPromise = this.cropAreaService.getImageCrops(this.props.imageUrl);
    imageCropsPromise.then((value) => {
      if (value.md5) {
        this.props.setMD5(value.md5.toString());
      } else {
        this.props.setMD5('');
      }
    }).catch(this.props.setMD5(''));

    const cancelableImageCropsPromise = makePromiseCancelable(imageCropsPromise);

    // Make it available in the component so it can be canceled somewhere else
    this.cancelGetImageCrops = () => {
      cancelableImageCropsPromise.cancel();
      this.cancelGetImageCrops = null;
    };

    return cancelableImageCropsPromise;
  }

  /**
   * Takes the cropper instance, applies the rotation degree set in the store by the toolbar,
   * and generates a single crop with full size of the original unrotated image. The resulting
   * image will be set in the store to replace the original, old crop boxes will be cleaned up
   * and the rotation mode will be switched off.
   */
  getRotatedImageFromCropper() {
    const canvasData = {
      top: 0,
      left: 0,
      width: this.props.cropAreaSize.width,
      height: this.props.cropAreaSize.height,
    };

    // Reset canvas to be the exact size and position we displayed it in the rotation mode section
    this.cropper.setCanvasData(canvasData);

    this.cropper.rotate(this.props.rotationAmount);

    // Single crop with the size of the entire image
    this.cropper.setCropBoxData(canvasData);

    this.cropper.getCroppedCanvas({
      fillColor: this.rotatedImageFillColor,
    }).toBlob((blob) => {
      this.props.uploadImageUrl(window.URL.createObjectURL(blob));
      this.props.setCropData([]);
      this.props.setCropEndpointCalled(false);
      this.toggleRotationMode();
      this.props.setLoading(true);
    });
  }

  /**
   * It removes a specific region style from the provided regions array.
   * @param {Array} regions the regions array.
   * @param {Number} regionIndex
   */
  removeCropRegionStyle(regions, regionIndex) {
    delete regions[regionIndex].data.regionStyle;
  }

  /**
   * It checks if the regions provided contain the needed properties to work.
   *
   * @param {Object[]} regions the regions to check.
   * @returns {Boolean} Will return `false` if, at least, one item does not contain the
   *                    needed data to work. `true` otherwise.
   */
  regionsContainNeededData(regions) {
    const attributesCheck = ['data', 'height', 'width', 'x', 'y'];
    const containsNeededData = attributesCheck.every(check =>
      regions.every(region => Object.keys(region).indexOf(check) !== -1));
    return containsNeededData;
  }

  /**
   * It checks if any region object in the provided `regions` array is being changed
   * and is not new.
   * @param {Object[]} regions the regions to check.
   * @returns {Boolean} Will return `true` if, at least, one item is being changed and
   *                    the `new` attribute is `false` or `undefined`. `false` otherwise.
   */
  isAnyCropRegionOnEdition(regions) {
    return regions.some(region =>
      this.isCropRegionChanging(region) && (!this.isCropRegionNew(region) || region.new === undefined));
  }

  /**
   * It checks if any region object in the provided `regions` array is a new region.
   * @param {Object[]} regions the regions to check.
   * @returns {Boolean} Will return `true` if, at least, one item is new. `false` otherwise.
   */
  isAnyCropRegionNew(regions) {
    return regions.some(region => this.isCropRegionNew(region));
  }


  /**
   * Translates the dimensions to pixels.
   * Every region provide their dimensions in percentages, so they need to be translated to pixels.
   * The following info is included:
   *    x: its position on the x axis
   *    y: its position on the y axis
   *    width: its width in %
   *    height: its height in %
   *
   * @param {Object} region The region to check
   */
  translateCropRegionDimensions(region) {
    // Find the crop area image which acts as a base for the percetage calculations
    const [imageElement] = document.getElementsByClassName('wtfCropArea_image');

    // Get the dimensions in pixels
    const translatedCropDimensions =
      this.cropAreaService.translatePercentagesToPixels(region, imageElement.width, imageElement.height);

    return translatedCropDimensions;
  }

  /**
   * Remove all data related to the selection of regions.
   */
  unselectAllRegions() {
    const cropData = this.getUnstyledRegions(this.props.cropData);
    this.props.setSelectedCropRegion({ selectedCropRegion: -1, cropData });
    this.props.setVerticalCropSelected(false);
  }

  /**
   * Select a specific region.
   * This method does two thing:
   *  a) It adds a data.regionStyle property to allow specific rules on the selected region (box-shadow and z-index)
   *  b) Saves the necessary data to the store
   *
   * @param {Object[]} regions the regions to process
   * @param {Number} index The selected region index
   */
  selectRegion(regions, index) {
    const region = regions[index];
    region.data.regionStyle = {
      boxShadow: '0 0 0 99999px rgba(0,0,0,0.6)',
      zIndex: '1',
    };

    this.props.setSelectedCropRegion({ selectedCropRegion: index, cropData: regions });

    // If the selected region is vertical, show a tooltip over the rotate button
    if (!this.isCropRegionChanging(region)) {
      this.props.setVerticalCropSelected(this.isSelectedCropRegionVertical(region));
    }
  }

  /**
   * This method checks that the aspect ratio of the crop region is 0.5 or less.
   * This means its height is twice as much as its width
   *
   * @param {Object} region The region to check
   * @returns {Boolean} whether the region provided is vertical or not
   */
  isSelectedCropRegionVertical(region) {
    // Get the pixel dimensions of the crop region (the lib provides percentages so they need to be translated)
    const dimensions = this.translateCropRegionDimensions(region);

    // Calculate the aspect ratio of the crop region provided
    const ratio = dimensions.width / dimensions.height;

    // Consider a crop region to be vertical when its height is twice as much as its width
    return ratio <= 0.5;
  }

  /**
   * Checks if a given region is contains the isChanging property (when clicking, for example).
   * @param {Object} region
   * @property {Boolean} isChanging
   * @returns {Boolean}
   */
  regionContainsChangingProperty(region) {
    return region.isChanging !== undefined;
  }

  /**
   * Checks if a given region is changing (resizing, for example).
   * @param {Object} region
   * @property {Boolean} isChanging
   * @returns {Boolean}
   */
  isCropRegionChanging(region) {
    return region.isChanging;
  }

  /**
   * Checks if a given region is a new one.
   * @param {Object} region
   * @property {Boolean} new
   * @returns {Boolean}
   */
  isCropRegionNew(region) {
    return region.new;
  }

  /**
   * Checks if a given region is unaltered / static / not being actively changed
   * right now.
   * @param {Object} region
   * @returns {Boolean}
   */
  isCropRegionUnaltered(region) {
    return !this.isCropRegionNew(region) && !this.isCropRegionChanging(region);
  }

  /**
   * Checks if a given region is being changed or if it's new.
   * @param {Object} region
   * @returns {Boolean}
   */
  isCropRegionBeingAltered(region) {
    return this.isCropRegionNew(region) || this.isCropRegionChanging(region);
  }

  /**
   * Checks if any region object in the provided `regions` array is being changed
   * right now. Will return `true` if, at least, one item is being changed. `false`
   * otherwise.
   * @param {Object[]} regions
   * @return {Boolean}
   */
  isAnyCropRegionChanging(regions) {
    return regions.some(this.isCropRegionBeingAltered);
  }

  /**
   * Given a region object, this function will check if its "height" or "width" values
   * are not below a minimum threshold. This will prevent the user from creating a
   * 0 by 0 polygon.
   * @param {Object} region
   * @property {Number} height
   * @property {Number} width
   * @returns {Object} The `region` parameter with updated dimensions.
   */
  updateRegionMinSize(region) {
    // Only update the regions being actively changed
    if (this.isCropRegionUnaltered(region)) {
      return region;
    }

    // These values are being handled as percentages, so we need to transform them in pixels
    const heightPixels = (region.height * this.props.cropAreaSize.height) / 100;
    const widthPixels = (region.width * this.props.cropAreaSize.width) / 100;

    // If they are below the minimum, correct that value using a percentage
    if (heightPixels < this.minimumCropSize) {
      region.height = (this.minimumCropSize * 100) / this.props.cropAreaSize.height;
    }

    if (widthPixels < this.minimumCropSize) {
      region.width = (this.minimumCropSize * 100) / this.props.cropAreaSize.width;
    }

    return region;
  }

  /**
   * Sets the `id` data of a given region object with a UUID v4 string.
   * @param {Object} region
   * @property {String} data.id
   * @returns {Object} The `region` parameter with updated ID.
   */
  updateRegionUUID(region) {
    region.data.id = getUUID();
    return region;
  }

  /**
   * Checks if all regions in a given region array passes all minimum data requirements.
   * If not, said objects will be updated to comply with those minimums.
   * - Minimum dimension (height, width)
   * - UUID for newly created regions
   * @param {Object[]} regions
   * @returns {Object[]} The `regions` parameter with all updated values.
   */
  updateRegionRequirements(regions) {
    return regions.map((region) => {
      region = this.updateRegionMinSize(region);

      if (this.isCropRegionNew(region)) {
        this.updateRegionUUID(region);
      }

      return region;
    });
  }

  /**
   * Remove extra info that the 3rd party lib  adds.
   * The properties isChanging and isNew are not needed.
   * Besides, since the lin adds those properties everytime the user interacts with the regions,
   * we can then use those properties to know if a region was accessed by the user or not
   * @param {Object[]} regions
   * @returns {Object[]} the regions withouth the extra data
   */
  removeExtraDataFromRegions(regions) {
    return regions.map((region) => {
      delete region.isChanging;
      delete region.isNew;

      return region;
    });
  }

  /**
   * Checks if any of the crop regions in a given array is the one being created at the moment
   * and saves it in the store via `setSelectedCropRegion` action.
   * @param {Object[]} regions
   */
  selectNewCropIfAny(regions) {
    const newCropIndex = regions.findIndex(this.isCropRegionNew);
    const changingCropIndex = regions.findIndex(this.regionContainsChangingProperty);

    regions = this.getUnstyledRegions(regions);

    if (newCropIndex !== -1) {
      this.selectRegion(regions, newCropIndex);
    } else if (changingCropIndex !== -1) {
      this.selectRegion(regions, changingCropIndex);
    }
  }

  /**
   * Changes the `drawingMode` flag in the store to the opposite of its current
   * value.
   */
  toggleDrawingMode() {
    this.props.setDrawingMode(!this.props.drawingMode);
  }

  /**
   * Callback for when the crop icon in the toolbar gets clicked or receives a key press.
   * It will:
   * - Cancel any pending text recognition calls to the BE
   * - Toggle the drawing mode flag
   * - Check if it's in drawing mode and remove any previously selected crop area
   */
  handleCropButtonInteraction() {
    if (this.cancelGetImageCrops) {
      this.cancelGetImageCrops();
    }

    if (!this.props.drawingMode) {
      this.unselectAllRegions();
    }

    this.toggleDrawingMode();
  }

  /**
   * Changes the `rotationMode` flag in the store to the opposite of its current value.
   * If the resulting operation ends up switching off the rotation mode, this method will
   * also reset the rotation amount to 0.
   */
  toggleRotationMode() {
    const targetRotationMode = !this.props.rotationMode;

    if (!targetRotationMode) {
      this.props.setRotationAmount(0);
    }

    this.props.setRotationMode(targetRotationMode);
  }

  /**
   * Callback for when the rotate icon in the toolbar gets clicked or receives a key press.
   * It will toggle the rotation mode flag.
   */
  handleRotateButtonInteraction() {
    this.toggleRotationMode();
  }

  /**
   * Callback for when each of the rotation buttons in the toolbar gets clicked.
   * Expects the amount of degrees the image should rotate, and will increment the value in the
   * store based on the incoming one.
   * @param {Number} degrees
   */
  handleIncrementalRotation(degrees) {
    const rotationTarget = this.props.rotationAmount + degrees;
    this.props.setRotationAmount(rotationTarget);
  }

  /**
   * Callback for when the "cancel rotation" button in the toolbar gets clicked.
   * It will switch off the rotation mode.
   */
  handleCancelRotation() {
    this.toggleRotationMode();
  }

  /**
   * Method that takes the crop area and generates a cropper instance based on that
   * image and the selected crop region.
   */
  createCropperFromCropArea() {
    const [image] = document.getElementsByClassName('wtfCropArea_image');

    this.cropper = new Cropper(image, {
      viewMode: 1,
      minContainerHeight: 1,
      minContainerWidth: 1,
      ready: this.getCroppedImage.bind(this),
    });
  }

  /**
   * Method that takes the rotated image and generates a cropper instance based on
   * that image and its rotation value.
   */
  createCropperFromRotatedImage() {
    const [image] = document.getElementsByClassName('wtfRotatedImage_image');

    this.cropper = new Cropper(image, {
      viewMode: 0,
      minContainerHeight: 1,
      minContainerWidth: 1,
      ready: this.getRotatedImageFromCropper.bind(this),
    });
  }

  /**
   * Callback that's executed when the search icon in the toolbar gets clicked or receives a key press.
   * If the rotation mode is enabled, it will create a cropper based on the rotation value. Otherwise it will
   * create a cropper based on the crop area and selected crop region.
   */
  handleSearchButtonInteraction() {
    if (this.props.rotationMode) {
      this.createCropperFromRotatedImage();
    } else {
      this.createCropperFromCropArea();
    }
  }

  /**
   * Clear store errorCode.
   */
  clearError() {
    this.props.setError(NO_ERROR);
  }

  /**
   * Triggers the identification process by calling the CropAreaService. Once the data
   * from backend arrives, it will transform it into the format expected by the CropArea
   * component and store that data using the `setCropData` prop.
   */
  identifyTextAndSetCropRegions() {
    // If we already have the BE data, do nothing
    if (!this.props.cropEndpointCalled) {
      this.clearError();
      this.props.setLoading(true);

      // Hide the Rotate tooltip while loading
      this.props.setVerticalCropSelected(false);

      this.getImageCrops().then((backendCrops) => {
        const targetImageSize = this.props.cropAreaSize;
        this.props.setIsAnyRegionDeleted(false);

        // If array is empty, there is no crop data for the image.
        if (backendCrops.data.length) {
          const regions = this.cropAreaService.translateBackendCropsToCropAreaRegions(
            backendCrops.data,
            this.props.imageDimensions,
            targetImageSize
          );
          this.props.setInitialRegionData(regions);

          // Once the data is available, select the largest crop region
          const selectedRegion = this.getLargestCropRegionIndex(regions);
          this.selectRegion(regions, selectedRegion);
        } else {
          this.props.setError(NO_DATA_FOUND_FOR_IMAGE_ERROR);
        }
        this.props.setLoading(false);
        this.props.setNextButtonTooltipDisplay(true);
        this.props.setCropEndpointCalled(true);
      }).catch((rejectReason) => {
        if (rejectReason.canceled) {
          this.props.setError(CANCELED_AUTO_RECOGNITION_ERROR);
        } else {
          this.props.setError(DEFAULT);
        }
        this.props.setLoading(false);
        this.props.setNextButtonTooltipDisplay(true);
        this.props.setCropEndpointCalled(true);
      });
    }
  }

  /**
   * Renders either the CropArea component or RotatedImage based on the rotationMode flag.
   * @return {ReactElement}
   */
  renderRotatedImageOrCropArea() {
    let renderData = null;

    if (this.props.rotationMode) {
      renderData = <RotatedImage {...this.getRotatedImageProps()} />;
    } else {
      renderData = <CropArea {...this.getCropAreaProps()} />;
    }

    return renderData;
  }

  render() {
    return (
      <div className='wtfCropAreaContainer'>
        {this.renderRotatedImageOrCropArea()}
        <div className='wtfCropAreaContainer_cropToolbar'>
          <CropToolbar {...this.getCropToolbarProps()} />
        </div>
      </div>
    );
  }
}

/**
 * Set the CropAreaContainer component props default values.
 * @type {Object}
 */
CropAreaContainer.defaultProps = {
  cropEndpointCalled: false,
  cropAreaSize: {},
  cropData: [],
  initialRegionData: [],
  md5: '',
  isAnyRegionDeleted: false,
  annotationIndex: 0,
  selectedCropRegion: -1,
  drawingMode: false,
  errorCode: '',
  imageUrl: '',
  imageDimensions: {},
  loading: false,
  verticalCropSelected: false,
  showNextButtonEnabledTooltip: true,
  rotationAmount: 0,
  rotationMode: false,
  setVerticalCropSelected: () => {},
  setNextButtonTooltipDisplay: () => {},
  redirectToUpload: () => {},
  redirectToResult: () => {},
  setCropEndpointCalled: () => {},
  setCropAreaSize: () => {},
  setCropData: () => {},
  setInitialRegionData: () => {},
  setMD5: () => {},
  setIsAnyRegionDeleted: () => {},
  setAnnotationIndex: () => {},
  setDrawingMode: () => {},
  setError: () => {},
  setLoading: () => {},
  setCroppedImageUrl: () => {},
  setResultRequestFlag: () => {},
  setResultData: () => {},
  setSelectedCropRegion: () => {},
  setRotationAmount: () => {},
  setRotationMode: () => {},
  uploadImageUrl: () => {},
};

/**
 * Declare the CropAreaContainer component PropTypes for better validation.
 * @type {Object}
 * @property {Object}   cropAreaSize                  Width and height of the crop area zone. Required to make the
 *                                                    calculations for the crop areas in comparison with the
 *                                                    original image.
 * @property {Object[]} cropData                      Array of objects containing the regions for the
 *                                                    CropArea component.
 * @property {String}   md5                           MD5 of font in cropped region.
 * @property {Number}   annotationIndex               Annotation Index of the cropped Image
 * @property {Boolean}  drawingMode                   Flag that tells if the crop area is in draw mode or not.
 *                                                    That is, if it's allowed to create crop boxes or not.
 * @property {String}   errorCode                     The code for an error triggered by the app.
 * @property {String}   imageUrl                      Image url for the crop area.
 * @property {Object}   imageDimensions               Dimensions in pixels of the uploaded image.
 * @property {Boolean}  loading                       It defines whether the loading component should be displayed.
 * @property {Number}   rotationAmount                Amount of degrees the cropped image is being rotated.
 * @property {Boolean}  rotationMode                  Flag that tells if the cropper is in rotation mode or not.
 * @property {Function} redirectToResult              This callback will redirect to the results page when necessary.
 * @property {Function} redirectToUpload              This callback will redirect to the upload page when necessary.
 * @property {Function} setCropEndpointCalled         Function that sets the flag in the store that tells if the crop
 *                                                    endpoint has been called.
 * @property {Function} setCropAreaSize               Callback that updates the crop area size in the store.
 * @property {Function} setCropData                   This callback will update the crop data once the BE call is made.
 * @property {Function} setMD5                        This callback will update the md5 of font in cropped image once
 *                                                    BE call is made.
 * @property {Function} setAnnotationIndex            This callback will update the Annotation Index of cropped image
 *                                                    once BE call is made.
 * @property {Function} setDrawingMode                Callback that sets the drawing mode flag in the store.
 * @property {Function} setError                      Callback that sets an error code in the store.
 * @property {Function} setLoading                    This callback will enable and disable the loading when needed.
 * @property {Function} setCroppedImageUrl            This callback will upload the cropped image url when it is ready.
 * @property {Function} setResultData                 Callback that updates the crop area list data in the store.
 * @property {Function} setResultRequestFlag          Callback that updates the results request flag.
 * @property {Function} setRotationAmount             Action function that sets the amunt of degrees the cropped image
 *                                                    is being rotated.
 * @property {Function} setRotationMode               Action function that sets the rotation flag in the store.
 * @property {Function} uploadImageUrl                Action function that will set the rotated image in the store.
 * @property {Function} setVerticalCropSelected       Callback to update the display value of the Rotate Button Tooltip
 * @property {Function} setNextButtonTooltipDisplay   Callback to update the display value of the Next Button Tooltip
 */
CropAreaContainer.propTypes = {
  cropEndpointCalled: PropTypes.bool,
  cropAreaSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  cropData: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    data: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
    }),
  })),
  md5: PropTypes.string,
  isAnyRegionDeleted: PropTypes.bool,
  annotationIndex: PropTypes.number,
  initialRegionData: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    data: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
    }),
  })),
  selectedCropRegion: PropTypes.number,
  drawingMode: PropTypes.bool,
  errorCode: PropTypes.string,
  imageUrl: PropTypes.string,
  imageDimensions: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
  }),
  loading: PropTypes.bool,
  showNextButtonEnabledTooltip: PropTypes.bool,
  verticalCropSelected: PropTypes.bool,
  rotationAmount: PropTypes.number,
  rotationMode: PropTypes.bool,
  redirectToUpload: PropTypes.func,
  redirectToResult: PropTypes.func,
  setCropEndpointCalled: PropTypes.func,
  setCropAreaSize: PropTypes.func,
  setCropData: PropTypes.func,
  setInitialRegionData: PropTypes.func,
  setMD5: PropTypes.func,
  setIsAnyRegionDeleted: PropTypes.func,
  setAnnotationIndex: PropTypes.func,
  setDrawingMode: PropTypes.func,
  setError: PropTypes.func,
  setLoading: PropTypes.func,
  setCroppedImageUrl: PropTypes.func,
  setResultRequestFlag: PropTypes.func,
  setResultData: PropTypes.func,
  setSelectedCropRegion: PropTypes.func,
  setVerticalCropSelected: PropTypes.func,
  setNextButtonTooltipDisplay: PropTypes.func,
  setRotationAmount: PropTypes.func,
  setRotationMode: PropTypes.func,
  uploadImageUrl: PropTypes.func,
};

/**
 * It dispatches store action and returns an object of actions that will be merged with component props.
 * @param   {Object} dispatch bind action creators.
 * @returns {Object}
 */
const mapDispatchToProps = dispatch => ({
  redirectToUpload: () => dispatch(push(appRoutes.upload)),
  redirectToResult: () => dispatch(push(appRoutes.result)),
  setCropEndpointCalled: flag => dispatch(setCropEndpointCalled(flag)),
  setCropAreaSize: size => dispatch(setCropAreaSize(size)),
  setCropData: cropData => dispatch(setCropData(cropData)),
  setMD5: value => dispatch(setMD5(value)),
  setIsAnyRegionDeleted: value => dispatch(setIsAnyRegionDeleted(value)),
  setInitialRegionData: value => dispatch(setInitialRegionData(value)),
  setAnnotationIndex: value => dispatch(setAnnotationIndex(value)),
  setSelectedCropRegion: selectedCropRegionData => dispatch(setSelectedCropRegion(selectedCropRegionData)),
  setDrawingMode: drawingMode => dispatch(setDrawingMode(drawingMode)),
  setError: error => dispatch(setError(error)),
  setLoading: value => dispatch(setLoading(value)),
  setCroppedImageUrl: value => dispatch(setCroppedImageUrl(value)),
  setResultRequestFlag: value => dispatch(setResultRequestFlag(value)),
  setResultData: value => dispatch(setResultData(value)),
  setVerticalCropSelected: value => dispatch(setVerticalCropSelected(value)),
  setNextButtonTooltipDisplay: value => dispatch(setNextButtonTooltipDisplay(value)),
  setRotationAmount: value => dispatch(setRotationAmount(value)),
  setRotationMode: value => dispatch(setRotationMode(value)),
  uploadImageUrl: value => dispatch(uploadImageUrl(value)),
});

/**
 * Connect component with the store and actions.
 * @type {Object}
 */
export default connect(
  state => ({
    cropEndpointCalled: state.crop.cropEndpointCalled,
    cropAreaSize: state.crop.cropAreaSize,
    cropData: state.crop.cropData,
    initialRegionData: state.crop.initialRegionData,
    md5: state.crop.md5,
    annotationIndex: state.crop.annotationIndex,
    isAnyRegionDeleted: state.crop.isAnyRegionDeleted,
    selectedCropRegion: state.crop.selectedCropRegion,
    drawingMode: state.crop.drawingMode,
    errorCode: state.crop.errorCode,
    imageDimensions: state.upload.imageDimensions,
    imageUrl: state.upload.imageUrl,
    loading: state.crop.loading,
    verticalCropSelected: state.crop.verticalCropSelected,
    showNextButtonEnabledTooltip: state.crop.cropToolbar.showNextButtonEnabledTooltip,
    rotationAmount: state.crop.rotationAmount,
    rotationMode: state.crop.rotationMode,
  }),
  mapDispatchToProps
)(CropAreaContainer);



// WEBPACK FOOTER //
// ./src/components/Crop/CropAreaContainer/CropAreaContainer.js